import { useState, useCallback, useEffect, type FormEvent } from 'react';
import { useApi } from '@/hooks/useApi';

interface CustomerOption {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface CreateKycModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedCustomerId?: number | null;
}

export default function CreateKycModal({ open, onClose, onSuccess, preselectedCustomerId }: CreateKycModalProps) {
  const { post, get } = useApi();
  const [customerId, setCustomerId] = useState(preselectedCustomerId ? String(preselectedCustomerId) : '');
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLoadingCustomers(true);
      get('/customers')
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data?.customers || [];
          setCustomers(list.map((c: Record<string, unknown>) => ({
            id: Number(c.id),
            firstName: String(c.firstName || ''),
            lastName: String(c.lastName || ''),
            email: String(c.email || ''),
          })));
        })
        .catch(() => setCustomers([]))
        .finally(() => setLoadingCustomers(false));
    }
  }, [open, get]);

  useEffect(() => {
    if (open && preselectedCustomerId) {
      setCustomerId(String(preselectedCustomerId));
    }
  }, [open, preselectedCustomerId]);

  const handleClose = useCallback(() => {
    setError(null);
    setCustomerId(preselectedCustomerId ? String(preselectedCustomerId) : '');
    setNotes('');
    onClose();
  }, [onClose, preselectedCustomerId]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!customerId.trim() || isNaN(Number(customerId))) {
      setError('Valid Customer ID is required');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      await post('/kyc', {
        customerId: Number(customerId.trim()),
        notes: notes.trim() || undefined,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create KYC request');
      setLoading(false);
    }
  }, [customerId, notes, post, onSuccess, handleClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <h2 className="text-lg font-bold text-stone-900">New KYC Request</h2>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">Customer *</label>
            <select
              required
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              disabled={loadingCustomers || !!preselectedCustomerId}
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white disabled:bg-stone-100 disabled:text-stone-500"
            >
              <option value="">{loadingCustomers ? 'Loading customers...' : 'Select a customer...'}</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName} ({c.email})
                </option>
              ))}
            </select>
            {customers.length === 0 && !loadingCustomers && (
              <p className="text-stone-400 text-xs mt-1.5">
                <i className="ri-information-line text-stone-400 mr-1" />
                No customers available.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">Internal Notes</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 resize-none"
              placeholder="Optional notes for this KYC request..."
            />
            <p className="text-stone-300 text-[10px] text-right mt-0.5">{notes.length}/2000</p>
          </div>

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Request'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}